import { useEffect, useRef } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { getAuthGeneration } from 'utils/auth';
import { getNativeCapabilities, isNativeMobile, nativeListen, type NativeListener } from './nativeBridge';
import { hasPushRegistration, syncRotatedPushToken } from './mobileNotifications';

/** Retain rotation handling after the initial permission/registration dialog has completed. */
export function MobilePushTokenSync() {
  const { user, isAuthLoading } = useAuth();
  const current = useRef({ userId: user?.id, isAuthLoading });
  current.current = { userId: user?.id, isAuthLoading };
  useEffect(() => {
    if (!isNativeMobile()) return;
    let active = true;
    let listener: NativeListener | undefined;
    let pending = Promise.resolve();
    void nativeListen<{ value: string }>('PushNotifications', 'registration', (event) => {
      const userId = current.current.userId;
      if (!active || current.current.isAuthLoading || !userId || !hasPushRegistration(userId) || typeof event.value !== 'string') return;
      const generation = getAuthGeneration();
      // Serial writes prevent a slower old token request from replacing a newer registration.
      pending = pending.catch(() => undefined).then(async () => {
        if (!active || current.current.userId !== userId || generation !== getAuthGeneration()) return;
        const capabilities = await getNativeCapabilities();
        if (!active || current.current.userId !== userId || current.current.isAuthLoading || generation !== getAuthGeneration() || !capabilities) return;
        await syncRotatedPushToken(event.value, capabilities, userId, generation);
      }).catch(() => undefined);
    }).then((handle) => {
      listener = handle;
      if (!active) void handle.remove().catch(() => undefined);
    }).catch(() => undefined);
    return () => { active = false; void listener?.remove().catch(() => undefined); };
  }, []);
  return null;
}
